import { COLOR_FORMATS, type TColorFormat } from "../constants/colors";

/**
 * Parse hex string to RGB (0-255).
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = hex.replace(/^#/, "").match(/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) return null;

  let hexDigits = match[1];
  if (hexDigits.length === 3) {
    hexDigits = hexDigits
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const r = parseInt(hexDigits.slice(0, 2), 16);
  const g = parseInt(hexDigits.slice(2, 4), 16);
  const b = parseInt(hexDigits.slice(4, 6), 16);
  return { r, g, b };
}

/**
 * RGB (0-255) to hex (#RRGGBB).
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.round(Math.max(0, Math.min(255, n)))
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Converts hex to rgba string. Alpha in [0, 1].
 */
export function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "rgba(0,0,0,0)";
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

/**
 * RGB (0-255) to HSL. H in [0, 360), S and L in [0, 1].
 */
export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s: number;
  const l = (max + min) / 2;

  if (max === min) {
    s = 0;
  } else {
    const chroma = max - min;
    s = l > 0.5 ? chroma / (2 - max - min) : chroma / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / chroma + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / chroma + 2) / 6;
        break;
      default:
        h = ((r - g) / chroma + 4) / 6;
    }

    h *= 360;
  }

  return { h, s, l };
}

/**
 * HSL to RGB (0-255). H in [0, 360), S and L in [0, 1].
 */
export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h = (((h % 360) + 360) % 360) / 360;

  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = Math.round(l * 255);
  } else {
    // One RGB channel from hue; hueOffset in [0, 1].
    const hueToChannel = (p: number, q: number, hueOffset: number) => {
      if (hueOffset < 0) hueOffset += 1;
      if (hueOffset > 1) hueOffset -= 1;
      if (hueOffset < 1 / 6) return p + (q - p) * 6 * hueOffset;
      if (hueOffset < 1 / 2) return q;
      if (hueOffset < 2 / 3) return p + (q - p) * (2 / 3 - hueOffset) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = Math.round(hueToChannel(p, q, h + 1 / 3) * 255);
    g = Math.round(hueToChannel(p, q, h) * 255);
    b = Math.round(hueToChannel(p, q, h - 1 / 3) * 255);
  }
  return { r, g, b };
}

// - Hex ↔ HSL (via RGB)

export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return rgbToHsl(rgb.r, rgb.g, rgb.b);
}

export function hslToHex(h: number, s: number, l: number): string {
  const { r, g, b } = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

/** Format current hex as display string for the given mode. */
export function formatColorForMode(hex: string, mode: TColorFormat): string {
  const rgb = hexToRgb(hex);

  if (!rgb) return hex;

  switch (mode) {
    case COLOR_FORMATS.HEX:
      return hex;
    case COLOR_FORMATS.RGB:
      return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    case COLOR_FORMATS.HSL: {
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      return `hsl(${Math.round(hsl.h)}, ${Math.round(hsl.s * 100)}%, ${Math.round(hsl.l * 100)}%)`;
    }
    default:
      return hex;
  }
}

/**
 * Validate and normalize a hex color string.
 * Accepts #RGB, #RRGGBB, RGB, RRGGBB (case-insensitive).
 */
export function validateHexInput(value: string): { valid: true; hex: string } | { valid: false; error: string } {
  const trimmed = value.trim();

  if (!trimmed) {
    return { valid: false, error: "validation.required_field" };
  }

  const rgb = hexToRgb(trimmed);

  if (!rgb) {
    return { valid: false, error: "validation.color_invalid" };
  }

  return { valid: true, hex: rgbToHex(rgb.r, rgb.g, rgb.b) };
}
